import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/components/ui/sonner';
import { Zap, AlertCircle, CheckCircle2 } from 'lucide-react';
import { bulkRegenerateQuizzes } from '@/services/contentManagement';
import { restartQuizGeneration } from '@/services/restartQuizGeneration';
import { restartPracticeGeneration } from '@/services/restartPracticeGeneration';
import { Alert, AlertDescription } from '@/components/ui/alert';

export const BulkOperationsPanel = () => {
  const [operation, setOperation] = useState<'quiz' | 'practice' | null>(null);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<any>(null);

  const handleBulkRegenerateQuizzes = async () => {
    try {
      setOperation('quiz');
      setProgress(0);
      setResults(null);

      toast.info('Starting bulk quiz regeneration...');
      
      const result = await restartQuizGeneration();
      
      setResults(result);
      setProgress(100);
      toast.success('Bulk quiz regeneration complete!');
    } catch (error) {
      toast.error('Failed to bulk regenerate quizzes');
      console.error(error);
    } finally {
      setOperation(null);
    }
  };

  const handleBulkRegeneratePractice = async () => {
    try {
      setOperation('practice');
      setProgress(0);
      setResults(null);

      toast.info('Starting bulk practice lab regeneration...');
      
      const result = await restartPracticeGeneration();
      
      setResults(result);
      setProgress(100);
      toast.success('Bulk practice lab regeneration complete!');
    } catch (error) {
      toast.error('Failed to bulk regenerate practice labs');
      console.error(error);
    } finally {
      setOperation(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Bulk Operations
        </CardTitle>
        <CardDescription>
          Perform bulk operations on content generation
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Bulk operations will regenerate content for all modules. This may take several minutes.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-3">
            <h4 className="font-medium">Bulk Regenerate Quizzes</h4>
            <p className="text-sm text-muted-foreground">
              Regenerate quizzes for all modules that failed or need updates
            </p>
            <Button
              onClick={handleBulkRegenerateQuizzes}
              disabled={!!operation}
              className="w-full"
              variant="outline"
            >
              {operation === 'quiz' ? 'Regenerating...' : 'Start Quiz Regeneration'}
            </Button>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium">Bulk Regenerate Practice Labs</h4>
            <p className="text-sm text-muted-foreground">
              Regenerate practice labs for all modules that failed or need updates
            </p>
            <Button
              onClick={handleBulkRegeneratePractice}
              disabled={!!operation}
              className="w-full"
              variant="outline"
            >
              {operation === 'practice' ? 'Regenerating...' : 'Start Practice Regeneration'}
            </Button>
          </div>
        </div>

        {operation && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Progress</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} />
          </div>
        )}

        {results && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-900">
              <strong>Operation Complete!</strong>
              <div className="mt-2 text-sm">
                {results.message || 'Regeneration started successfully'}
                {results.operationId && (
                  <p className="mt-1 font-mono text-xs">
                    Operation ID: {results.operationId}
                  </p>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
